import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Form from 'react-bootstrap/Form'
import { selectTranslations } from '../../../services/i18n/i18nSlice'
import { setLoading } from '../../../services/global/globalSlice'
import { fetchCreateSubject, fetchDeleteSubject, fetchDownloadSubjectsAndTopics, fetchEditSubject, fetchQuestionCountsByTopicIds } from '../QuestionBankApi'
import SubjectModal from './SubjectModal'
import ConfirmModal from '../../../components/_modals/ConfirmModal'
import trash_img from '../../../assets/images/trash.svg'
import download_img from '../../../assets/images/download.svg'

const EMPTY_FORM = { subject_name: '', icon_url: null }

export default function SubjectsTab(props) {
    const t = useSelector(selectTranslations)
    const dispatch = useDispatch()

    const subjects = props.subjects
    const topics = props.topics
    const [questionCountsBySubject, setQuestionCountsBySubject] = useState({})
    const [loadingCounts, setLoadingCounts] = useState({})
    const [search, setSearch] = useState('')

    const [showModal, setShowModal] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [formData, setFormData] = useState(EMPTY_FORM)

    const [confirmModal, setConfirmModal] = useState({
        show: false, title: '', text: '', onClose: null, onConfirm: null, confirmCta: '', closeCta: ''
    })

    const filteredSubjects = useMemo(() => {
        if (!search) return subjects
        const s = search.toLowerCase()
        return subjects.filter(sub => (sub?.subject_name || '').toLowerCase().includes(s))
    }, [search, subjects])

    const loadQuestionCount = async (subject_id) => {
        setLoadingCounts(prev => ({ ...prev, [subject_id]: true }))
        const subjectTopicIds = topics.filter(topic => topic.subject_id === subject_id).map(topic => topic.topic_id)
        const countsResponse = await fetchQuestionCountsByTopicIds(subjectTopicIds)
        if (countsResponse.success) {
            const total = subjectTopicIds.reduce((sum, topic_id) => sum + (countsResponse.data[topic_id] || 0), 0)
            setQuestionCountsBySubject(prev => ({ ...prev, [subject_id]: total }))
        }
        setLoadingCounts(prev => ({ ...prev, [subject_id]: false }))
    }

    const openNewSubject = () => {
        setEditingId(null)
        setFormData(EMPTY_FORM)
        setShowModal(true)
    }

    const openEditSubject = (subject) => {
        setEditingId(subject?.subject_id)
        setFormData({ subject_name: subject?.subject_name || '', icon_url: subject?.icon_url || null })
        setShowModal(true)
    }

    const closeModal = () => setShowModal(false)

    const handleFormChange = (field, value) => {
        setFormData({ ...formData, [field]: value })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        dispatch(setLoading(true))
        const response = editingId
            ? await fetchEditSubject(editingId, formData)
            : await fetchCreateSubject(formData)
        if (response.success) {
            await props.onDataChange?.()
            closeModal()
        } else {
            toast.error(t.error.error_loading)
        }
        dispatch(setLoading(false))
    }

    const closeConfirmModal = () => setConfirmModal({ ...confirmModal, show: false })

    const performDeleteSubject = async (subject_id, force_delete = false) => {
        dispatch(setLoading(true))
        const response = await fetchDeleteSubject(subject_id, force_delete)
        dispatch(setLoading(false))
        if (response.success) {
            toast.success(t.question_bank.deleted_subject)
            closeConfirmModal()
            await props.onDataChange?.()
            return
        }
        if (!force_delete && response.error?.includes('used in')) {
            setConfirmModal({
                show: true,
                title: t.question_bank.force_delete_title,
                text: t.question_bank.force_delete_text,
                onClose: closeConfirmModal,
                onConfirm: () => performDeleteSubject(subject_id, true),
                confirmCta: t.question_bank.force_delete_cta,
                closeCta: t.modals.no_exit
            })
            return
        }
        toast.error(t.error.error_loading)
    }

    const downloadSubjectsAndTopics = async () => {
        dispatch(setLoading(true))
        const response = await fetchDownloadSubjectsAndTopics()
        if (response.success) {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `materie_topic_${new Date().toLocaleDateString("it-IT")}.xlsx`)
            document.body.appendChild(link)
            link.click()
        } else {
            toast.error(t.question.error_while_downloading)
        }
        dispatch(setLoading(false))
    }

    const handleDeleteSubject = (subject_id) => {
        setConfirmModal({
            show: true,
            title: t.question_bank.delete_subject_title,
            text: t.question_bank.delete_subject_text,
            onClose: closeConfirmModal,
            onConfirm: () => performDeleteSubject(subject_id, false),
            confirmCta: t.actions.delete,
            closeCta: t.modals.no_exit
        })
    }

    return (
        <section>
            <div className="info_card mb-3">
                <h2>{t.question_bank.search_filters}</h2>
                <div className="d-flex align-items-end flex-wrap mt-2">
                    <Form.Group className="mb-2" controlId="subject_search">
                        <Form.Label>{t.question_bank.search}</Form.Label>
                        <Form.Control type="text" placeholder={t.question_bank.search_placeholder} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </Form.Group>
                </div>
            </div>

            <div className="d-flex justify-content-end mb-3">
                <button type="button" className="secondary me-2" onClick={downloadSubjectsAndTopics}>
                    {t.question_bank.download_subjects_and_topics}
                    <img src={download_img} className="ms-1" alt="" />
                </button>
                <button onClick={openNewSubject}>{t.question_bank.new_subject}</button>
            </div>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                        <tr>
                            <th>{t.question_bank.subject_name}</th>
                            <th>{t.question_bank.available_questions}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredSubjects?.length ? filteredSubjects.map((s) => (
                            <tr key={s.subject_id}>
                                <td>{s.subject_name}</td>
                                <td>
                                    {questionCountsBySubject[s.subject_id] !== undefined ? questionCountsBySubject[s.subject_id] : (
                                        <button type="button" className="secondary" disabled={!!loadingCounts[s.subject_id]} onClick={() => loadQuestionCount(s.subject_id)}>
                                            {loadingCounts[s.subject_id] ? t.question_bank.calculating : t.question_bank.calculate}
                                        </button>
                                    )}
                                </td>
                                <td>
                                    <div className="d-flex">
                                        <button type="button" className="me-2" onClick={() => props.onViewTopics?.(s.subject_id)}>{t.question_bank.view_topics}</button>
                                        <button className="secondary me-2" onClick={() => openEditSubject(s)}>{t.actions.open}</button>
                                        <button className="secondary" onClick={() => handleDeleteSubject(s.subject_id)}>
                                            <img src={trash_img} alt="" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={3} className="text-center">{t.question_bank.no_subjects}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <SubjectModal
                show={showModal}
                isEditing={!!editingId}
                formData={formData}
                onChange={handleFormChange}
                onSubmit={handleSubmit}
                onClose={closeModal}
            />

            <ConfirmModal props={confirmModal} />
        </section>
    )
}
